import React, { useState, useEffect } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import axios from 'axios';
import microsoftLogo from '../../images/microsoftLogo.png';
import { alert, AlertType } from '@quyva/shared-ui';
import { getUrl } from '../../utils';
import { useAuth } from '../../context';
import { Config } from 'config/envConfig';

/**
 * Microsoft Sign-In Component
 * Handles Microsoft SSO authentication with MSAL
 */

const SCOPES = [
    'email',
    'openid',
    'profile',
    'offline_access',
    'User.Read',
    'User.ReadBasic.All',
    'Organization.Read.All',
    'MultiTenantOrganization.Read.All',
];

// MSAL Configuration (will be fetched from backend)
let msalConfig = null;
let msalInstance = null;

const MicrosoftSignInButton = ({ onSuccess, onError, currency = 'INR' }) => {
    const { setMicrosoftAuthInstance } = useAuth();
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setMicrosoftAuthInstance(instance);
    }, [instance]);

    /**
     * Handle Microsoft Sign-In
     */
    const handleMicrosoftSignIn = async () => {
        setIsLoading(true);

        try {
            // 1. Initiate Microsoft login popup
            const loginResponse = await instance.loginPopup({
                scopes: SCOPES,
                prompt: 'select_account',
            });

            console.log('✅ Microsoft login successful:', loginResponse);

            // 2. Get access token for Graph API
            const tokenResponse = await instance.acquireTokenSilent({
                scopes: SCOPES,
                account: loginResponse.account,
            });

            // console.log('✅ Access token acquired');

            // 3. Fetch user profile from Microsoft Graph API
            const userProfile = await fetchUserProfile(
                tokenResponse.accessToken
            );
            // console.log('✅ User profile fetched:', userProfile);

            // 4. Fetch organization data from Microsoft Graph API
            const organizationData = await fetchOrganizationData(
                tokenResponse.accessToken
            );
            // console.log('✅ Organization data fetched:', organizationData);

            // 5. Send to backend for authentication
            const authResult = await authenticateWithBackend(
                userProfile,
                organizationData,
                currency
            );

            // console.log('✅ Backend authentication successful:', authResult);

            // 6. Call success callback
            if (onSuccess) {
                onSuccess(authResult);
            }
        } catch (error) {
            console.error('❌ Microsoft sign-in error:', error);

            if (onError) {
                onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle Sign Out
     */
    const handleSignOut = async () => {
        try {
            await instance.logoutPopup();
            // console.log('✅ Signed out successfully');
        } catch (error) {
            alert(AlertType.ERROR, error.message);
            console.error('❌ Sign out error:', error);
        }
    };

    return (
        <>
            {!isAuthenticated ? (
                <button
                    onClick={handleMicrosoftSignIn}
                    disabled={isLoading}
                    className='w-full rounded-lg border px-3 py-2.5 mt-1  flex justify-center items-center text-texts-secondary200 shadow-inner hover:bg-backgrounds-primary100'
                >
                    <span>
                        <img
                            className='max-w-3 max-h-3 mr-2'
                            src={microsoftLogo}
                            alt='camera'
                            draggable='false'
                        />
                    </span>
                    {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
                </button>
            ) : (
                <button
                    onClick={handleSignOut}
                    disabled={isLoading}
                    className='w-full rounded-lg border px-3 py-2.5 mt-1  flex justify-center items-center text-texts-secondary200 shadow-inner hover:bg-backgrounds-primary100'
                >
                    <span>
                        <img
                            className='max-w-3 max-h-3 mr-2'
                            src={microsoftLogo}
                            alt='camera'
                            draggable='false'
                        />
                    </span>
                    Sign out
                </button>
            )}
        </>
    );
};

/**
 * Fetch user profile from Microsoft Graph API
 */
const fetchUserProfile = async (accessToken) => {
    try {
        const response = await axios.get(
            'https://graph.microsoft.com/v1.0/me',
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('❌ Error fetching user profile:', error);
        throw new Error('Failed to fetch user profile from Microsoft');
    }
};

/**
 * Fetch organization data from Microsoft Graph API
 */
const fetchOrganizationData = async (accessToken) => {
    try {
        const response = await axios.get(
            'https://graph.microsoft.com/v1.0/organization',
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );

        // Returns a list of organizations, usually just one
        if (
            response.data &&
            response.data.value &&
            response.data.value.length > 0
        ) {
            return response.data.value[0];
        }

        return null;
    } catch (error) {
        console.error(
            '⚠️ Error fetching organization data (may not have permission):',
            error
        );
        // Organization data is optional, return null if not available
        return null;
    }
};

/**
 * Authenticate with backend API
 */
const authenticateWithBackend = async (
    userProfile,
    organizationData,
    currency
) => {
    try {
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/auth/microsoft-signin`
        );
        const response = await axios.post(
            url,
            {
                userProfile,
                organizationData,
                currency,
            },
            {
                withCredentials: true, // Send cookies
            }
        );

        return response.data;
    } catch (error) {
        console.error('❌ Backend authentication error:', error);

        if (error.response) {
            throw new Error(
                error.response.data.message || 'Authentication failed'
            );
        }

        throw new Error('Network error during authentication');
    }
};

/**
 * Initialize MSAL and return the configured instance
 */
const initializeMSAL = async () => {
    try {
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/auth/microsoft-auth-config`
        );
        // Fetch Microsoft auth config from backend
        const response = await axios.get(url);

        const { config } = response.data;

        msalConfig = {
            auth: {
                clientId: config.clientId,
                authority: config.authority,
                redirectUri: config.redirectUri,
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false,
            },
        };

        msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();

        console.log('✅ MSAL initialized successfully');
        return msalInstance;
    } catch (error) {
        alert(AlertType.ERROR, 'Error initializing MSAL: ' + error.message);
        console.error('❌ Error initializing MSAL:', error);
        throw error;
    }
};

/**
 * Main wrapper component that initializes MSAL
 */
export const MicrosoftSignIn = ({ onSuccess, onError, currency }) => {
    const [msalReady, setMsalReady] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const init = async () => {
            try {
                await initializeMSAL();
                setMsalReady(true);
            } catch (err) {
                setError('Failed to initialize Microsoft authentication');
                console.error(err);
            }
        };

        init();
    }, []);

    if (error) {
        alert(AlertType.ERROR, error);
        return (
            // <button
            //     onClick={() => window.location.reload()}
            //     className='w-full rounded-lg border px-3 py-2.5 mt-1  flex justify-center items-center text-texts-secondary200 shadow-inner hover:bg-backgrounds-primary100'
            // >
            //      <span>
            //         <img
            //             className='max-w-3 max-h-3 mr-2'
            //             src={microsoftLogo}
            //             alt='camera'
            //             draggable='false'
            //         />
            //     </span>
            //     Retry Microsoft Sign-In
            // </button>
            null
        );
    }

    if (!msalReady) {
        return (
            <button
                onClick={() => {}}
                disabled={!msalReady}
                className='w-full rounded-lg border px-3 py-2.5 mt-1  flex justify-center items-center text-texts-secondary200 shadow-inner hover:bg-backgrounds-primary100'
            >
                <span>
                    <img
                        className='max-w-3 max-h-3 mr-2'
                        src={microsoftLogo}
                        alt='camera'
                        draggable='false'
                    />
                </span>
                Loading Microsoft Signin...
            </button>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            <MicrosoftSignInButton
                onSuccess={onSuccess}
                onError={onError}
                currency={currency}
            />
        </MsalProvider>
    );
};
